import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ImageLightbox } from './ImagePreview';
import { cn } from '../../lib/utils';

export type StackImage = {
  src: string;
  alt: string;
};

interface StackedImagesProps {
  images: StackImage[];
  cardWidth?: number;
  cardHeight?: number;
}

const spring = { type: 'spring', stiffness: 320, damping: 26, mass: 0.7 } as const;

export function StackedImages({ images, cardWidth = 200, cardHeight = 125 }: StackedImagesProps) {
  const [hovered, setHovered] = useState(false);
  const [activeLightbox, setActiveLightbox] = useState<number | null>(null);

  if (images.length === 0) {
    return null;
  }

  if (images.length === 1) {
    const image = images[0];
    return (
      <>
        <button
          type="button"
          onClick={() => setActiveLightbox(0)}
          style={{ width: cardWidth, height: cardHeight }}
          className="group relative block overflow-hidden rounded-lg border border-border bg-card shadow-sm outline-none ring-ring/50 focus-visible:ring-2"
        >
          <img
            src={image.src}
            alt={image.alt}
            crossOrigin="anonymous"
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </button>
        <AnimatePresence>
          {activeLightbox === 0 && (
            <ImageLightbox
              images={images}
              open={true}
              onOpenChange={(open) => setActiveLightbox(open ? 0 : null)}
            />
          )}
        </AnimatePresence>
      </>
    );
  }

  const count = images.length;
  const spread = Math.min(cardWidth * 0.66, 150);
  const peek = 7;
  const hoverLift = 8;
  const hoverScale = 1.04;

  const expandedWidth = cardWidth + spread * (count - 1);
  const collapsedWidth = cardWidth + peek * (count - 1);

  // Room for the count badge, per-card hover lift/scale, and collapsed stack offsets.
  const padTop = hoverLift + Math.ceil((cardHeight * (hoverScale - 1)) / 2) + 4;
  const padRight = 12;
  const padBottom = Math.ceil((count - 1) * peek * 0.5) + 4;
  const contentWidth = hovered ? expandedWidth : collapsedWidth;

  return (
    <>
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        className="relative overflow-visible"
        style={{ height: cardHeight + padTop + padBottom }}
        animate={{ width: contentWidth + padRight }}
        transition={spring}
      >
        {images.map((image, i) => {
          const zIndex = count - i;

          const collapsed = {
            x: i * peek,
            y: i * peek * 0.5,
            rotate: i === 0 ? 0 : (i % 2 === 0 ? 1.5 : -1.5) * i,
            scale: 1 - i * 0.04,
          };
          const expanded = {
            x: i * spread,
            y: 0,
            rotate: 0,
            scale: 1,
          };

          return (
            <motion.button
              key={`${image.src}-${i}`}
              type="button"
              onClick={() => setActiveLightbox(i)}
              className={cn(
                'absolute left-0 top-0 block overflow-hidden rounded-lg border border-border bg-card shadow-lg outline-none ring-ring/50 focus-visible:ring-2',
                'transition-shadow duration-200 hover:shadow-xl',
              )}
              style={{
                width: cardWidth,
                height: cardHeight,
                top: padTop,
                zIndex,
              }}
              initial={false}
              animate={hovered ? expanded : collapsed}
              whileHover={
                hovered ? { y: -hoverLift, scale: hoverScale, zIndex: count + 1 } : undefined
              }
              transition={{ ...spring, delay: hovered ? i * 0.04 : 0 }}
            >
              <img
                src={image.src}
                alt={image.alt}
                crossOrigin="anonymous"
                className="pointer-events-none h-full w-full object-cover object-top"
              />
              <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-background"
                animate={{ opacity: hovered ? 0 : i === 0 ? 0 : Math.min(i * 0.14, 0.45) }}
                transition={spring}
              />
            </motion.button>
          );
        })}

        <motion.span
          className="pointer-events-none absolute z-50 flex h-6 min-w-6 items-center justify-center rounded-full border border-border bg-primary px-1.5 text-xs font-medium text-primary-foreground shadow-md"
          animate={{ opacity: hovered ? 0 : 1, scale: hovered ? 0.6 : 1 }}
          transition={spring}
          style={{ left: cardWidth - 10, top: padTop - 10 }}
        >
          {count}
        </motion.span>
      </motion.div>

      {/* One viewer for the whole stack: clicking a card opens it on that card
          and the arrows page through the rest without closing the dialog. */}
      <AnimatePresence>
        {activeLightbox !== null && images[activeLightbox] && (
          <ImageLightbox
            images={images}
            initialIndex={activeLightbox}
            open={true}
            onOpenChange={(open) => setActiveLightbox(open ? activeLightbox : null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
